import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ECalendarValue, IDatePickerConfig } from 'ng2-date-picker';
import { InputType } from '../transforming-input/transforming-input.component';
import { AutocompleteOption } from '../../field-text/field-text.component';
import { SnackBarService } from 'src/app/share/snack-bar.service';
import { ITagsArray } from '../../search/search';
import { DynamicViewField } from 'piwe-front-swagger-client';

interface TableRow {
  [key: string]: any;
}

@Component({
  selector: 'app-single-cms-input',
  templateUrl: './single-cms-input.component.html',
  styleUrls: ['./single-cms-input.component.scss']
})
export class SingleCmsInputComponent implements OnInit {
  public InputType = InputType;
  public wasSubmited: boolean = false;

  @Input() autocompleteType: string | undefined = '';
  @Input() controlName: string = '';
  @Input() form!: FormGroup;
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() type: InputType = InputType.Text;
  @Input() selectOptions: { [key: string]: any } | undefined = {};
  @Input() isEditModeActive: boolean = false;

  @Input() tableFields?: DynamicViewField[] = [];

  modalForm?: FormGroup;
  isModalOpen = false;
  isEditing = false;
  editingIndex: number | null = null;

  autocompleteOption?: AutocompleteOption;
  public selectWasFocused: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private snackBarService: SnackBarService
  ) {
    this.modalForm = this.formBuilder.group({});
  }


  get tableData(): TableRow[] {
    return this.form.get(this.controlName)?.value || [];
  }


  openAddModal(): void {
    this.isEditing = false;
    this.editingIndex = null;
    this.initModalForm();
    this.isModalOpen = true;
  }

  editRow(index: number): void {
    this.isEditing = true;
    this.editingIndex = index;
    this.initModalForm();
    this.modalForm?.patchValue(this.tableData[index]);
    this.isModalOpen = true;
  }

  deleteRow(index: number): void {
    const newData = [...this.tableData];
    newData.splice(index, 1);
    this.form.get(this.controlName)?.setValue(newData);
    this.isModalOpen = false;
  }

  private initModalForm(): void {
    const group: any = {};
    this.tableFields?.forEach(field => {
      group[field!.name!] = ['', Validators.required];
    });
    this.modalForm = this.formBuilder.group(group);
  }

  saveRow(): void {
    if (this.modalForm?.valid) {
      const newData = [...this.tableData];
      if (this.isEditing && this.editingIndex !== null) {
        newData[this.editingIndex] = this.modalForm.value;
      } else {
        newData.push(this.modalForm.value);
      }
      this.form.get(this.controlName)?.setValue(newData);
      this.closeModal();
    }
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.modalForm = undefined;
  }

  formatTableValue(value: any, field: DynamicViewField): string {
    if (!field?.kind || !value) {
      return '-';
    }

    if (field.kind === 'DROPDOWN' && field.dropdown_options) {
      const option = field.dropdown_options.find(opt => opt.id === value);
      return option?.title || value?.toString() || '-';
    }

    if (field.kind === 'TAGS') {
      return value.map((v: any) => v.title).join(', ');
    }

    return value?.toString() || '-';
  }


  isFieldInvalid(fieldName: string): boolean {
    if (!this.modalForm) {
      return false;
    }
    const control = this.modalForm.get(fieldName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  dtConfig: IDatePickerConfig = {
    format: 'DD.MM.YYYY. HH:mm',
    firstDayOfWeek: 'mo',
    showTwentyFourHours: true
  };

  dtConfigMoment: IDatePickerConfig = {
    format: 'DD.MM.YYYY. HH:mm',
    firstDayOfWeek: 'mo',
    showTwentyFourHours: true,
    returnedValueType: ECalendarValue.Moment
  };


  ngOnInit(): void {
    // Provjera da li kontrola već postoji prije dodavanja nove
    if (!this.form.get(this.controlName)) {
      this.form.addControl(this.controlName, this.formBuilder.control(null));
    }

    // Dodajte listener za promjene na date picker poljima
    if (this.type === InputType.DateMoment) {
      const control = this.form.get(this.controlName);
      if (control) {
        control.valueChanges.subscribe(value => {
          // Vrijednost već dolazi kao Moment objekt zbog konfiguracije
          // Nema potrebe za dodatnom konverzijom
        });
      }
    }
  }

  isRequiredField() {
    const form_field = this.form!.get(this.controlName);

    if (form_field == null) {
      return false;
    }

    if (!form_field.validator) {
      return false;
    }

    const validator = form_field.validator({} as AbstractControl);
    return validator && validator.required;
  }

  changeAutocomplete(autocompleteOption?: AutocompleteOption) {
    this.autocompleteOption = autocompleteOption;
    this.form.get(this.controlName)?.setValue(this.autocompleteOption?.value);
  }

  onSelectFocus() {
    this.selectWasFocused = true;
  }

  passwordTouched: boolean = false;

  passwordFocus(focus: boolean) {
    if (focus) {
      if (!this.passwordTouched) {
        //this.form.get(this.controlName)?.setValue('');
      }
    } else {
      if (!this.passwordTouched) {
        //this.form.get(this.controlName)?.setValue('');
      }
    }
  }

  touchPassword() {
    this.passwordTouched = true;
  }

  handleFileInput(event: any): void {
    const reader = new FileReader();

    if (event.target.files && event.target.files.length) {
      const file = event.target.files[0];
      if (file.size > 2048000) {
        //maybe different file size?? 1 or 3mb
        //1024000
        //2048000
        //3072000
        this.snackBarService.showSnackBar(
          'File too large. Maximum file size is 2048KB.',
          'ERROR'
        );
        return;
      }
      reader.readAsDataURL(file);
      reader.onload = () => {
        this.form.patchValue({
          [this.controlName]: reader.result
        });

        // need to run CD since file load runs outside of zone
        // this.cd.markForCheck();
      };
    } else {
      this.snackBarService.showSnackBar('No file attached.', 'ERROR');
    }
  }

  getTags(): AutocompleteOption[] {
    return this.form.get(this.controlName)!.value ?? [];
  }

  saveTableRow(): void {
    if (this.modalForm?.valid) {
      const control = this.form.get(this.controlName);
      const currentData = [...(control?.value || [])];

      if (this.isEditing && this.editingIndex !== null) {
        currentData[this.editingIndex] = this.modalForm.value;
      } else {
        currentData.push(this.modalForm.value);
      }

      control?.setValue(currentData);
      control?.markAsDirty();
      control?.markAsTouched();

      this.closeModal();
    } else {
      this.modalForm?.markAllAsTouched();
      this.snackBarService.showSnackBar('Please fill in all required fields', 'ERROR');
    }
  }

  getFieldInputType(field: DynamicViewField): InputType {
    switch (field.kind) {
      case 'STRING':
      case 'TEXT':
        return InputType.Text;
      case 'INTEGER':
      case 'DECIMAL':
        return InputType.Number;
      case 'BOOLEAN':
        return InputType.Boolean;
      case 'DROPDOWN':
        return InputType.Select;
      case 'TAGS':
        return InputType.Tags;
      case 'DATETIME':
        return InputType.Date;
      default:
        return InputType.Text;
    }
  }

  transformingInputType(field: DynamicViewField): InputType {
    if (field === undefined) {
      return InputType.Disabled;
    }

    if (field.is_read_only) {
      return InputType.Disabled;
    }

    return this.inputTypeForFieldKind(field.kind!);
  }

  inputTypeForFieldKind(fieldKind: string): InputType {
    switch (fieldKind) {
      case 'STRING':
      case 'TEXT':
        return InputType.Text;
      case 'BOOLEAN':
        return InputType.Boolean;
      case 'INTEGER':
        return InputType.Number;
      case 'DECIMAL':
        return InputType.Number;
      case 'DROPDOWN':
        return InputType.Select;
      case 'TAGS':
        return InputType.Tags;
      case 'DATETIME':
        return InputType.Date2;
      case 'INLINE_TABLE':
        return InputType.InlineTable;
      default:
        return InputType.Text;
    }
  }

  isReadOnly(field: DynamicViewField): boolean {
    return !(field.is_read_only ?? false);
  }

  getSelectOptions(field: DynamicViewField): any {
    // Check for static dropdown options
    if (field.dropdown_options && Array.isArray(field.dropdown_options)) {
      const dict: any = {};
      for (const option of field.dropdown_options) {
        dict[option.id] = option.title;
      }
      return dict;
    }

    return undefined;
  }

  autocompleteTypeForField(field: DynamicViewField): any {
    return field.autocomplete;
  }
}
